import type {
  CanvasManualAdjustmentModel,
  CanvasPromptRunRecordModel,
  FieldMapping,
  MappingResponse,
  SchemaEnrichmentSnapshotModel,
  TransformationRule,
  TransformationStep,
  CanvasAggregationExecutionPlanModel,
} from '../../services/api';
import type { CanvasSelection, CanvasProvenanceDetails } from '../types';
import type { ReviewItem } from '../hooks/useReviewItems';
import type { ValidatorRule, ValidatorOverrideEntry } from './FieldRules';

export interface TransformationFormValues {
  transformation: string;
  explanation: string;
  confidence: number | null;
  isRequired: boolean;
  defaultValue: string;
  removeTransformation: boolean;
  stepDescription: string;
  operation: string;
  parameters?: Record<string, unknown>;
  aiGenerated: boolean;
  sourceFile?: string;
  sourceField?: string;
  targetTable?: string;
  targetField?: string;
}

export type InspectorPanelProps = {
  selection: CanvasSelection | null;
  mappingResponse?: MappingResponse;
  onSave: (mapping: FieldMapping, values: TransformationFormValues, activeStep: TransformationStep | undefined) => void;
  saving: boolean;
  error?: string | null;
  transformationRules?: TransformationRule[];
  onApplyRule?: (mapping: FieldMapping, step: TransformationStep, rule: TransformationRule) => void;
  applyingRuleId?: string | null;
  statusMessage?: { type: 'success' | 'error'; message: string } | null;
  manualAdjustments?: CanvasManualAdjustmentModel[];
  aggregationPlan?: CanvasAggregationExecutionPlanModel[];
  schemaEnrichment?: SchemaEnrichmentSnapshotModel;
  onSaveSchemaEnrichment?: (input: {
    targetKind: 'schema_table' | 'schema_field';
    targetId: string;
    enrichment: Record<string, unknown>;
    annotations?: Record<string, unknown>;
  }) => Promise<void>;
  onUndoAdjustment?: (adjustmentId: string) => Promise<void>;
  onRedoAdjustment?: (adjustmentId: string) => Promise<void>;
  undoingAdjustmentId?: string | null;
  redoingAdjustmentId?: string | null;
  edgeProvenanceByMapping?: Record<string, EdgeProvenanceEntry[]>;
  promptRuns?: CanvasPromptRunRecordModel[];
  sampleOutput?: Record<string, Array<Record<string, unknown>>>;
  // Issue #656: field-scoped review surfaces (when a target field/edge is selected).
  /** Pre-filtered review items addressing the selected field. */
  fieldReviewItems?: ReviewItem[];
  /** Validator rules declared on the selected field's column. */
  fieldValidatorRules?: ValidatorRule[];
  /** Accepted overrides on the selected field, from `recipe.validator_overrides`. */
  fieldValidatorOverrides?: ValidatorOverrideEntry[];
  /** True when the selected column is part of the table's primary key. */
  fieldIsPrimaryKey?: boolean;
  /** Batch identifier — required for the inline decision-resolve mutation. */
  batchId?: string;
  /** Disables resolve actions when the recipe is in a non-editable lifecycle state. */
  resolveDisabled?: boolean;
  /** Trigger preview materialization when sample_output is empty. */
  onMaterializeSamples?: () => void;
  materializing?: boolean;
};

export type EdgeProvenanceEntry = {
  id: string;
  label?: string;
  kind?: string;
  confidence?: number;
  aiGenerated?: boolean;
  provenance?: CanvasProvenanceDetails;
  metadata?: Record<string, unknown>;
  status?: string;
};

export type ManualAdjustmentCardProps = {
  adjustment: CanvasManualAdjustmentModel;
  onUndo?: (adjustmentId: string) => Promise<void>;
  onRedo?: (adjustmentId: string) => Promise<void>;
  undoingId?: string | null;
  redoingId?: string | null;
};

export type TemplateChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';

export const emptyForm: TransformationFormValues = {
  transformation: '',
  explanation: '',
  confidence: null,
  isRequired: true,
  defaultValue: '',
  removeTransformation: false,
  stepDescription: '',
  operation: 'expression',
  parameters: undefined,
  aiGenerated: false,
};
